/** @file DSGVO Art. 20 Datenexport: baut ein JSON-Payload aus Plays + Teams und löst Download aus. */
/* global Blob */

/** @typedef {import("../types/schemas.js").PlayDoc} PlayDoc */
/** @typedef {import("../types/schemas.js").TeamDoc} TeamDoc */

/**
 * Aktuelle Export-Schema-Version. Muss mit dem Play-Schema synchron bleiben.
 */
export const EXPORT_SCHEMA_VERSION = 2;

/**
 * @typedef {Object} ExportUser
 * @property {string} uid
 * @property {string | null} email
 * @property {string | null} displayName
 */

/**
 * @typedef {Object} ExportPayload
 * @property {string} exportedAt ISO-8601 UTC timestamp.
 * @property {number} schemaVersion Always EXPORT_SCHEMA_VERSION.
 * @property {ExportUser} user
 * @property {PlayDoc[]} plays
 * @property {TeamDoc[]} teams
 */

/**
 * Deep-copy via JSON round-trip. Firestore Timestamp instances implement
 * `toJSON()` (→ `{ seconds, nanoseconds }`), so they serialize cleanly.
 * @template T
 * @param {T} value
 * @returns {T}
 */
function deepCopy(value) {
  return /** @type {T} */ (JSON.parse(JSON.stringify(value)));
}

/**
 * Baut das Export-Payload als reines Objekt ohne Seiteneffekte.
 *
 * @param {{ uid: string, email?: string | null, displayName?: string | null }} user
 * @param {PlayDoc[]} plays
 * @param {TeamDoc[]} teams
 * @returns {ExportPayload}
 */
export function buildExport(user, plays, teams) {
  return {
    exportedAt: new Date().toISOString(),
    schemaVersion: EXPORT_SCHEMA_VERSION,
    user: {
      uid: user.uid,
      email: user.email ?? null,
      displayName: user.displayName ?? null,
    },
    plays: Array.isArray(plays) ? deepCopy(plays) : [],
    teams: Array.isArray(teams) ? deepCopy(teams) : [],
  };
}

/**
 * Deterministisches Dateinamen-Format: `handballplan-export-<uid>-<YYYY-MM-DD>.json`.
 * Datum wird als UTC-Kalendertag formatiert (toISOString().slice(0,10)), damit der
 * Dateiname unabhängig von der lokalen TZ reproduzierbar ist.
 *
 * @param {string} uid
 * @param {Date} [date]
 * @returns {string}
 */
export function exportFilename(uid, date = new Date()) {
  const day = date.toISOString().slice(0, 10);
  return `handballplan-export-${uid}-${day}.json`;
}

/**
 * Erzeugt einen Client-seitigen JSON-Download des Exports.
 *
 * @param {{ uid: string, email?: string | null, displayName?: string | null }} user
 * @param {PlayDoc[]} plays
 * @param {TeamDoc[]} teams
 * @param {Document} [doc] injectable Document für Tests (jsdom).
 * @returns {void}
 */
export function downloadExport(user, plays, teams, doc = document) {
  const payload = buildExport(user, plays, teams);
  const json = JSON.stringify(payload, null, 2);
  const blob = new Blob([json], { type: "application/json;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = doc.createElement("a");
  a.href = url;
  a.download = exportFilename(user.uid);
  a.rel = "noopener";
  doc.body.appendChild(a);
  a.click();
  doc.body.removeChild(a);
  // Nächster Tick: Blob-URL freigeben, damit der Browser den Download abschließen kann.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
